import { forwardRef, useEffect, useState } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouter } from '@tanstack/react-router'
import {
  Bookmark,
  Building2,
  Filter,
  Link2,
  MapPin,
  Search,
  Tag,
} from 'lucide-react'
import { toast } from 'sonner'

import { Job } from '@/_gql/graphql'

import { useAuth } from '@/services/auth/use-auth'
import { GraphQLError } from '@/services/graphql/error'
import { gqlFetch } from '@/services/graphql/fetcher'
// Import GraphQL queries and utilities
import { GqlBookmarkJob } from '@/services/job/gql'
import { queryJobList, queryJobTagList } from '@/services/job/query'

import { toIdrWithoutRp } from '@/lib/utils/number'

import { filterJobs, getFilterTag } from '../../-utils/filter-job'
import DialogMessageError from './-components/dialog-message-error'

interface JobListingProps {
  ref?: HTMLDivElement | null
  id?: string
}

// Enhanced Job interface with bookmark status
interface EnhancedJob extends Omit<Job, 'is_bookmark'> {
  is_bookmark: boolean
}

// Bookmark mutation hook
const useUpdateJobBookmark = () => {
  const queryClient = useQueryClient()
  const [isDialogErrorOpen, setIsDialogErrorOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')

  const { mutateAsync: updateJobBookmark, isPending: isUpdatingBookmark } =
    useMutation({
      mutationFn: async ({
        job_id,
        value,
      }: {
        job_id: number
        value: boolean
      }) => {
        const result = await gqlFetch({
          query: GqlBookmarkJob,
          variables: {
            jobId: job_id,
            value: value,
          },
        })
        return { job_id, value, result }
      },
      onSuccess: (data) => {
        const message = data.value
          ? 'Pekerjaan berhasil disimpan!'
          : 'Pekerjaan dihapus dari simpanan!'
        toast.success(message)

        // Invalidate and refetch job list to get updated bookmark status
        queryClient.invalidateQueries({ queryKey: ['jobList'] })
        // Also invalidate related queries
        queryClient.invalidateQueries({ queryKey: ['jobs'] })
      },
      onError: (error) => {
        let msg = error.message
        if (GraphQLError.is(error)) {
          msg = error.getMessage()
        }
        toast.error(msg)
      },
    })

  return {
    updateJobBookmark,
    isPending: isUpdatingBookmark,
    isDialogErrorOpen,
    setIsDialogErrorOpen,
    dialogMessage,
  }
}

// Loading skeleton component
const JobCardSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-[#D4D4D4] bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center gap-4">
      <div className="h-12 w-12 rounded-lg bg-gray-200"></div>
      <div className="flex-1">
        <div className="mb-2 h-5 rounded bg-gray-200"></div>
        <div className="h-4 w-24 rounded bg-gray-200"></div>
      </div>
    </div>
    <div className="mb-4 flex gap-2">
      <div className="h-6 w-16 rounded-xl bg-gray-200"></div>
      <div className="h-6 w-20 rounded-xl bg-gray-200"></div>
    </div>
    <div className="mb-4 flex items-center gap-2">
      <div className="h-4 w-4 rounded bg-gray-200"></div>
      <div className="h-4 w-32 rounded bg-gray-200"></div>
    </div>
    <div className="h-4 w-40 rounded bg-gray-200"></div>
  </div>
)

const JobCard = ({ job }: { job: EnhancedJob }) => {
  const navigate = useNavigate({ from: '/' })
  const [localBookmarkState, setLocalBookmarkState] = useState(job.is_bookmark)
  const {
    updateJobBookmark,
    isPending: isUpdatingBookmark,
    isDialogErrorOpen,
    setIsDialogErrorOpen,
    dialogMessage,
  } = useUpdateJobBookmark()

  // Update local state when job prop changes
  useEffect(() => {
    setLocalBookmarkState(job.is_bookmark)
  }, [job.is_bookmark])

  // Use local state for immediate UI feedback
  const isBookmarked = localBookmarkState

  const handleJobClick = () => {
    navigate({
      to: `/job-detail/${job.id}`,
    })
  }

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation()
    const jobUrl = `${window.location.origin}/job-detail/${job.id}`
    navigator.clipboard.writeText(jobUrl)
    toast.success('Link berhasil disalin!')
  }

  const handleBookmarkToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const newBookmarkState = !isBookmarked

    // Optimistic update - immediately update UI
    setLocalBookmarkState(newBookmarkState)

    try {
      // Call mutation
      await updateJobBookmark({ job_id: job.id, value: newBookmarkState })
    } catch (error) {
      // Revert optimistic update on error
      setLocalBookmarkState(!newBookmarkState)
      console.error('Failed to update bookmark:', error)
    }
  }

  return (
    <>
      {/* Error Dialog */}
      <DialogMessageError
        msg={dialogMessage}
        open={isDialogErrorOpen}
        setOpen={setIsDialogErrorOpen}
      />

      <div
        onClick={handleJobClick}
        className="group relative cursor-pointer rounded-lg border border-[#D4D4D4] bg-white p-5 shadow-sm transition-all duration-300 hover:border-primary-yes hover:shadow-xl hover:shadow-[#D91F2E]/30"
      >
        {/* Action Buttons Container */}
        <div className="absolute right-4 top-4 z-10 flex gap-2">
          {/* Bookmark Button - Always visible */}
          <button
            onClick={handleBookmarkToggle}
            className={`rounded-full p-2 transition-all duration-300 ${
              isBookmarked
                ? 'bg-primary-yes text-white'
                : 'bg-gray-100 hover:bg-primary-yes hover:text-white'
            }`}
            title={isBookmarked ? 'Hapus dari simpanan' : 'Simpan pekerjaan'}
            disabled={isUpdatingBookmark}
          >
            <Bookmark
              size={16}
              className={isBookmarked ? 'fill-current' : ''}
            />
          </button>

          {/* Copy Link Button - Always visible */}
          <button
            onClick={handleCopyLink}
            className="rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-primary-yes hover:text-white"
            title="Salin link"
          >
            <Link2 size={16} />
          </button>
        </div>

        {/* Company Logo & Job Info */}
        <div className="mb-4 flex items-start gap-4 pr-20">
          <div className="flex-shrink-0">
            <img
              className="h-12 w-12 object-contain"
              src="/img/landing/logo.svg"
              alt="Company Logo"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-semibold leading-tight text-[#131517] transition-colors duration-300 group-hover:text-primary-yes">
              {job.title}
            </h3>
            <p className="mt-1 text-sm text-gray-600">YES Partner</p>
          </div>
        </div>

        {/* Job Skills */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {job &&
              job.job_skills &&
              job.job_skills.slice(0, 3).map((skill: any, index: any) => (
                <span
                  className="inline-block rounded-xl bg-[#FFF4F5] px-3 py-1 text-xs"
                  key={index}
                >
                  {skill.name}
                </span>
              ))}
            {job.job_skills && job.job_skills.length > 3 && (
              <span className="inline-block rounded-xl bg-gray-100 px-3 py-1 text-xs text-gray-600">
                +{job.job_skills.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="mb-4 flex items-center gap-2">
          <MapPin className="h-4 w-4 flex-shrink-0 text-primary-yes" />
          <span className="text-sm font-medium text-gray-700">
            {job.country.name}
          </span>
        </div>

        {/* Salary */}
        <div className="mb-2">
          <p className="font-semibold text-gray-900">
            IDR{' '}
            {`${toIdrWithoutRp(job?.min ?? 0)} - ${toIdrWithoutRp(job?.max ?? 0)}`}
          </p>
        </div>

        {/* Posted Date */}
        {job.created_at && (
          <div className="mb-4">
            <p className="text-xs text-gray-400">
              Diposting {new Date(job.created_at).toLocaleDateString('id-ID')}
            </p>
          </div>
        )}

        {/* Hover Effect Arrow */}
        <div className="absolute bottom-4 right-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-yes">
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </>
  )
}

const JobListing = forwardRef<HTMLDivElement, JobListingProps>(
  ({ id }, ref) => {
    const router = useRouter()
    const navigate = useNavigate({ from: '/' })

    const {
      data: dataTag,
      refetch: refetchTag,
      isLoading: isLoadingTags,
    } = useQuery(queryJobTagList())
    // Query job list with bookmark status
    const {
      data: dataJobListing,
      refetch: refetchJobListing,
      isLoading: isLoadingJobs,
    } = useQuery(queryJobList(true))

    const [isDialogErrorOpen, setIsDialogErrorOpen] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [activeMenuTab, setActiveMenuTab] = useState<string>('')
    const [listJob, setListJob] = useState<EnhancedJob[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const [searchTerm, setSearchTerm] = useState('')
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [jobsPerPage] = useState(12)
    const [metadataListJob, setMetadataListJob] = useState({
      page: 1,
      totalData: 0,
    })

    const user = useAuth((s) => s.user)

    const handleClickFilterTags = (
      tag: string,
      limit: number = jobsPerPage,
      page: number = 1
    ) => {
      setActiveMenuTab(tag)
      setCurrentPage(1)
      setSearchTerm('')

      if (dataJobListing) {
        // Ensure jobs have is_bookmark field - use the value from API
        const jobsWithBookmark = (dataJobListing as Job[]).map((job) => ({
          ...job,
          is_bookmark: job.is_bookmark ?? false,
        })) as EnhancedJob[]

        const dataFilter = filterJobs({
          jobs: jobsWithBookmark,
          tag,
          limit: limit * page,
          page: 1,
        })

        const data = dataFilter.data
        const totalData = dataFilter.totalData
        setListJob(data)
        setMetadataListJob({
          page: page,
          totalData: totalData,
        })
      }
    }

    const handleSearch = (searchValue: string) => {
      setSearchTerm(searchValue)
      setCurrentPage(1)

      if (dataJobListing && searchValue.trim()) {
        // Ensure jobs have is_bookmark field - use the value from API
        const jobsWithBookmark = (dataJobListing as Job[]).map((job) => ({
          ...job,
          is_bookmark: job.is_bookmark ?? false,
        })) as EnhancedJob[]

        const filteredJobs = jobsWithBookmark.filter(
          (job) =>
            job.title.toLowerCase().includes(searchValue.toLowerCase()) ||
            (job.country?.name &&
              job.country.name
                .toLowerCase()
                .includes(searchValue.toLowerCase()))
        )
        setListJob(filteredJobs)
        setMetadataListJob({
          page: 1,
          totalData: filteredJobs.length,
        })
      } else if (dataJobListing && activeMenuTab) {
        handleClickFilterTags(activeMenuTab)
      }
    }

    const loadMore = () => {
      const nextPage = currentPage + 1
      setCurrentPage(nextPage)

      if (dataJobListing && !searchTerm) {
        // Ensure jobs have is_bookmark field - use the value from API
        const jobsWithBookmark = (dataJobListing as Job[]).map((job) => ({
          ...job,
          is_bookmark: job.is_bookmark ?? false,
        })) as EnhancedJob[]

        const dataFilter = filterJobs({
          jobs: jobsWithBookmark,
          tag: activeMenuTab,
          limit: jobsPerPage * nextPage,
          page: 1,
        })

        setListJob(dataFilter.data)
        setMetadataListJob({
          page: nextPage,
          totalData: dataFilter.totalData,
        })
      }
    }

    useEffect(() => {
      if (
        dataTag &&
        dataTag[0] &&
        dataTag.length > 0 &&
        dataJobListing &&
        !activeMenuTab
      ) {
        if (dataTag && dataJobListing) {
          // Ensure jobs have is_bookmark field - use the value from API
          const jobsWithBookmark = (dataJobListing as Job[]).map((job) => ({
            ...job,
            is_bookmark: job.is_bookmark ?? false,
          })) as EnhancedJob[]

          const dataTagTemp = getFilterTag(jobsWithBookmark, dataTag)
          setActiveMenuTab(dataTagTemp[0]?.label ?? '')
        }
      }
    }, [dataTag, dataJobListing, activeMenuTab])

    useEffect(() => {
      if (dataJobListing && activeMenuTab && !searchTerm) {
        handleClickFilterTags(activeMenuTab)
      }
    }, [dataJobListing, activeMenuTab])

    useEffect(() => {
      refetchTag()
      refetchJobListing()
    }, [router.state.location.hash, refetchTag, refetchJobListing])

    const availableTags =
      dataTag && dataJobListing
        ? getFilterTag(
            (dataJobListing as Job[]).map((job) => ({
              ...job,
              is_bookmark: job.is_bookmark ?? false,
            })) as EnhancedJob[],
            dataTag
          )
        : []
    const isLoading = isLoadingTags || isLoadingJobs

    return (
      <>
        <DialogMessageError
          msg={dialogMessage}
          open={isDialogErrorOpen}
          setOpen={setIsDialogErrorOpen}
        />

        <div
          ref={ref}
          id={id}
          className="flex flex-col items-center px-4 py-8 md:px-8 md:py-14 lg:px-36"
        >
          {/* Header */}
          <div className="mb-8 text-center">
            <h2 className="mb-2 text-2xl font-semibold text-gray-900 md:text-3xl lg:text-4xl">
              Lowongan Kerja <span className="text-primary-yes">Terkini</span>
            </h2>
            <p className="max-w-2xl text-sm text-gray-600 md:text-base">
              Temukan peluang karir terbaik dari berbagai perusahaan terpercaya
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-6 w-full max-w-2xl">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Cari pekerjaan, perusahaan, atau lokasi..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-lg border border-[#D4D4D4] py-3 pl-10 pr-4 outline-none focus:border-transparent focus:ring-2 focus:ring-primary-yes"
              />
            </div>
          </div>

          {/* Filter Tags */}
          <div className="mb-8 w-full max-w-6xl">
            {/* Mobile Filter Toggle */}
            <div className="mb-4 md:hidden">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center rounded-lg border border-[#D4D4D4] px-4 py-2 hover:bg-gray-50"
              >
                <Filter size={18} className="mr-2" />
                Filter Kategori
              </button>
            </div>

            {/* Filter Tags */}
            <div className={`${isFilterOpen ? 'block' : 'hidden'} md:block`}>
              {isLoading ? (
                <div className="grid grid-flow-col gap-4 overflow-auto">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="h-10 w-32 animate-pulse whitespace-nowrap rounded-md bg-gray-200"
                    ></div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-flow-col gap-4 overflow-auto">
                  {availableTags.map((menu, key) => (
                    <div
                      key={key}
                      className={`${menu.label === activeMenuTab && !searchTerm ? 'bg-primary-yes text-white' : 'border border-[#D4D4D4] bg-white text-black'} flex cursor-pointer items-center justify-around gap-3 whitespace-nowrap rounded-md px-4 py-2 hover:bg-primary-yes hover:text-white`}
                      onClick={() => handleClickFilterTags(menu.label)}
                    >
                      {menu.label}
                      <div
                        className={`flex h-7 items-center justify-center rounded-md px-2 text-black`}
                        style={{ backgroundColor: menu.colorLabel }}
                      >
                        {menu.total}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Results Info */}
          {searchTerm && (
            <div className="mb-4 w-full max-w-6xl">
              <p className="text-gray-600">
                Menampilkan {listJob.length} hasil untuk "{searchTerm}"
              </p>
            </div>
          )}

          {/* Jobs Grid */}
          <div className="w-full max-w-6xl">
            {isLoading ? (
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                {Array.from({ length: 9 }).map((_, index) => (
                  <JobCardSkeleton key={index} />
                ))}
              </div>
            ) : listJob.length > 0 ? (
              <>
                <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
                  {listJob.slice(0, currentPage * jobsPerPage).map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>

                {/* Load More Button */}
                {!searchTerm && listJob.length > currentPage * jobsPerPage && (
                  <div className="text-center">
                    <button
                      onClick={loadMore}
                      className="rounded-lg bg-primary-yes px-8 py-3 font-medium text-white transition-colors duration-300 hover:bg-primary-yes/90"
                    >
                      Muat Lebih Banyak
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                  <Building2 className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-gray-600">
                  {searchTerm
                    ? 'Tidak ada hasil yang ditemukan'
                    : 'Belum ada lowongan'}
                </h3>
                <p className="text-gray-500">
                  {searchTerm
                    ? 'Coba ubah kata kunci pencarian Anda'
                    : 'Lowongan untuk kategori ini belum tersedia'}
                </p>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }
)

JobListing.displayName = 'JobListing'

export default JobListing
